import { motion } from "framer-motion";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { resolveMove } from "../game/helpers";
import { getMoveSoundEffectId, type SoundEffectId } from "../lib/soundEffects";
import {
  ROUND_ZERO_TUTORIAL_INITIAL_BOARD,
  ROUND_ZERO_TUTORIAL_INITIAL_HAND,
  ROUND_ZERO_TUTORIAL_STEPS,
  applyTutorialStepMove,
  type TutorialStep
} from "../game/tutorial";
import { createInstantPressHandlers } from "../lib/instantPress";
import type { BoardTile } from "../game/types";
import { getTileColor, type Theme } from "../theme";
import { Board, type PendingMove } from "./Board";
import { HandView, type HandTileView } from "./HandView";

type TutorialRoundProps = {
  theme: Theme;
  onComplete: () => void;
  onPlaySoundEffect: (soundEffectId: SoundEffectId) => void;
  onTrackProgress?: (step: string) => void;
};

const TUTORIAL_AUTO_ADVANCE_MS = 120;

type TutorialStepSpotlight = {
  handTileId: string | null;
  boardTileId: string | null;
};

function createTutorialHand(values: readonly number[]): HandTileView[] {
  return values.map((value, index) => ({
    id: `tutorial-h-${index}`,
    value
  }));
}

function createTutorialBoard(values: readonly number[]): BoardTile[] {
  return values.map((value, index) => ({
    id: `tutorial-b-${index}`,
    value,
    source: "dealt",
    createdTurn: 0,
    buildDepth: 0
  }));
}

function updateTutorialBoard(
  previousBoard: BoardTile[],
  boardIndex: number,
  builtValue: number | null
): BoardTile[] {
  return previousBoard.flatMap((tile, index) => {
    if (index !== boardIndex) {
      return [tile];
    }

    if (builtValue === null) {
      return [];
    }

    return [
      {
        ...tile,
        value: builtValue,
        source: "built",
        buildDepth: tile.buildDepth + 1
      }
    ];
  });
}

function captureTutorialStepSpotlight(
  step: TutorialStep,
  board: readonly BoardTile[],
  hand: readonly HandTileView[]
): TutorialStepSpotlight {
  return {
    handTileId: step.kind === "ack" ? null : hand[step.expectedHandIndex]?.id ?? null,
    boardTileId: step.kind === "tap_board" ? board[step.expectedBoardIndex]?.id ?? null : null
  };
}

function refillTutorialHand(
  previousHand: readonly HandTileView[],
  handIndex: number,
  refillTile: number | null,
  nextHandTileIdRef: MutableRefObject<number>
): HandTileView[] {
  const remainingHand = previousHand.filter((_, index) => index !== handIndex);

  if (refillTile === null) {
    return remainingHand;
  }

  return [
    ...remainingHand,
    {
      id: `tutorial-h-${nextHandTileIdRef.current++}`,
      value: refillTile
    }
  ];
}

export function TutorialRound({
  theme,
  onComplete,
  onPlaySoundEffect,
  onTrackProgress
}: TutorialRoundProps) {
  const initialBoard = createTutorialBoard(ROUND_ZERO_TUTORIAL_INITIAL_BOARD);
  const initialHand = createTutorialHand(ROUND_ZERO_TUTORIAL_INITIAL_HAND);
  const [stepIndex, setStepIndex] = useState(0);
  const [board, setBoard] = useState<BoardTile[]>(() => initialBoard);
  const [hand, setHand] = useState<HandTileView[]>(() => initialHand);
  const [stepSpotlight, setStepSpotlight] = useState<TutorialStepSpotlight>(() =>
    captureTutorialStepSpotlight(ROUND_ZERO_TUTORIAL_STEPS[0], initialBoard, initialHand)
  );
  const [selectedHandIndex, setSelectedHandIndex] = useState<number | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove>(null);
  const [isResolvingMove, setIsResolvingMove] = useState(false);
  const moveTimerRef = useRef<number | null>(null);
  const autoAdvanceTimerRef = useRef<number | null>(null);
  const nextHandTileIdRef = useRef(ROUND_ZERO_TUTORIAL_INITIAL_HAND.length);

  const step = ROUND_ZERO_TUTORIAL_STEPS[stepIndex];
  const selectedHandValue =
    selectedHandIndex === null ? null : hand[selectedHandIndex]?.value ?? null;
  const activeHandSpotlightTileId = stepSpotlight.handTileId;
  const activeBoardSpotlightTileId = stepSpotlight.boardTileId;
  const showHandTooltip = step.kind === "select_hand";
  const showBoardTooltip = step.kind === "tap_board";
  const showCompletionCallout = step.kind === "ack";
  const showTutorialScrim = true;
  const handRegionMuted = !showHandTooltip && activeHandSpotlightTileId === null;
  const completeButtonHandlers = createInstantPressHandlers<HTMLButtonElement>(onComplete);

  useEffect(() => {
    onTrackProgress?.(`${step.id}_shown`);
  }, [onTrackProgress, step]);

  useEffect(() => {
    return () => {
      if (moveTimerRef.current !== null) {
        window.clearTimeout(moveTimerRef.current);
      }

      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  const advanceToStep = (
    nextStepIndex: number,
    nextBoard: readonly BoardTile[] = board,
    nextHand: readonly HandTileView[] = hand
  ) => {
    const nextStep = ROUND_ZERO_TUTORIAL_STEPS[nextStepIndex];

    if (nextStep === undefined) {
      return;
    }

    setStepIndex(nextStepIndex);
    setStepSpotlight(captureTutorialStepSpotlight(nextStep, nextBoard, nextHand));
  };

  const handleSelectHandTile = (handIndex: number) => {
    if (isResolvingMove || step.kind !== "select_hand") {
      return;
    }

    if (handIndex !== step.expectedHandIndex) {
      return;
    }

    setSelectedHandIndex(step.expectedHandIndex);
    advanceToStep(stepIndex + 1);
  };

  const handleTapBoardTile = (boardIndex: number) => {
    if (isResolvingMove || step.kind !== "tap_board") {
      return;
    }

    if (selectedHandIndex !== step.expectedHandIndex || boardIndex !== step.expectedBoardIndex) {
      return;
    }

    const boardTile = board[boardIndex];
    const handTile = hand[selectedHandIndex];

    if (boardTile === undefined || handTile === undefined) {
      return;
    }

    const resolution = resolveMove(handTile.value, boardTile.value);
    if (resolution.outcome !== step.expectedOutcome) {
      return;
    }

    const soundEffectId = getMoveSoundEffectId(resolution.outcome);
    if (soundEffectId !== null) {
      onPlaySoundEffect(soundEffectId);
    }

    setPendingMove({
      boardIndex,
      outcome: resolution.outcome,
      particleColor: getTileColor(boardTile.value).hex
    });
    setIsResolvingMove(true);

    moveTimerRef.current = window.setTimeout(() => {
      const nextState = applyTutorialStepMove(
        board.map((tile) => tile.value),
        hand.map((tile) => tile.value),
        step
      );
      const nextBoard = updateTutorialBoard(
        board,
        boardIndex,
        resolution.clearsTile ? null : nextState.board[boardIndex] ?? null
      );
      const nextHand = refillTutorialHand(
        hand,
        step.expectedHandIndex,
        step.refillTile,
        nextHandTileIdRef
      );

      setBoard(nextBoard);
      setHand(nextHand);
      setSelectedHandIndex(null);
      setPendingMove(null);
      setIsResolvingMove(false);
      onTrackProgress?.(`${step.id}_completed`);
      moveTimerRef.current = null;

      autoAdvanceTimerRef.current = window.setTimeout(() => {
        advanceToStep(stepIndex + 1, nextBoard, nextHand);
        autoAdvanceTimerRef.current = null;
      }, TUTORIAL_AUTO_ADVANCE_MS);
    }, resolution.clearsTile ? 190 : 105);
  };

  return (
    <>
      {showTutorialScrim ? (
        <div className="flow-spotlight-scrim tutorial-spotlight-scrim" aria-hidden="true" />
      ) : null}
      <section className="board-stage tutorial-board-stage-spotlight">
        <div className="tutorial-board-shell">
          <Board
            board={board}
            flowCount={0}
            incrementLanes={[]}
            timerDisplay={null}
            timerPausedAt={null}
            timerSpotlightKind={null}
            incrementSpotlightTileId={null}
            floatingScores={[]}
            boardTopOverlay={
              showCompletionCallout ? (
                <div className="flow-spotlight-callout-position tutorial-complete-callout-position">
                  <motion.div
                    className="flow-spotlight-callout"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Tutorial complete"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <p className="flow-spotlight-copy">{step.text}</p>
                    <button
                      type="button"
                      className="flow-spotlight-button"
                      {...completeButtonHandlers}
                    >
                      OK
                    </button>
                  </motion.div>
                </div>
              ) : null
            }
            boardRowOverlay={
              showBoardTooltip ? (
                <div className="tutorial-board-callout-position">
                  <motion.div
                    className="flow-spotlight-callout"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Tutorial"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <p className="flow-spotlight-copy">{step.text}</p>
                  </motion.div>
                </div>
              ) : null
            }
            selectedHandValue={selectedHandValue}
            spotlightTileId={activeBoardSpotlightTileId}
            dimAllTiles={activeBoardSpotlightTileId === null}
            dimNonSpotlightTiles={activeBoardSpotlightTileId !== null}
            showMoveHints={false}
            stabilizeAnimations
            gameOver={false}
            isResolvingMove={isResolvingMove || step.kind === "ack"}
            pendingMove={pendingMove}
            theme={theme}
            onSelectTile={handleTapBoardTile}
            onDismissTimerSpotlight={() => {}}
          />
        </div>
      </section>

      <footer className="footer-stage tutorial-footer-stage tutorial-footer-stage-spotlight">
        {showHandTooltip ? (
          <div className="tutorial-hand-callout-position">
            <motion.div
              className="flow-spotlight-callout"
              role="dialog"
              aria-modal="true"
              aria-label="Tutorial"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <p className="flow-spotlight-copy">{step.text}</p>
            </motion.div>
          </div>
        ) : null}
        <div className={`hand-stage tutorial-hand-shell${handRegionMuted ? " tutorial-region-muted" : ""}`}>
          <HandView
            hand={hand}
            selectedHandIndex={selectedHandIndex}
            negativeSpotlightTileId={null}
            spotlightTileId={activeHandSpotlightTileId}
            dimNonSpotlightTiles={activeHandSpotlightTileId !== null}
            showSelectionStyle={false}
            stabilizeAnimations
            gameOver={false}
            isResolvingMove={isResolvingMove || step.kind === "ack"}
            hintText={null}
            theme={theme}
            onSelectHandTile={handleSelectHandTile}
            onDismissNegativeSpotlight={() => {}}
          />
        </div>
      </footer>
    </>
  );
}
